import { IssueCaseDoc, OrderDoc } from '@gooduncles/gu-app-schema';
import { Segmented } from 'antd';
import { FC, useCallback, useState } from 'react';

import StoreSearch from 'src/atomic-components/molecules/StoreSearch/StoreSearch';

import { useCrmBoardContext } from '../../CrmBoardContext';
import { useRecentIssueCases } from '../../hooks/useRecentIssueCases';
import { useRecentOrders } from '../../hooks/useRecentOrders';

import classes from './StoreSection.module.scss';

import RecentIssueCaseList from './RecentIssueCaseList';
import RecentOrderList from './RecentOrderList';

type TabKey = 'orders' | 'issues';

const StoreSection: FC = () => {
  const {
    selectedStore,
    setSelectedStore,
    linkedOrder,
    setLinkedOrder,
    linkedIssueCase,
    setLinkedIssueCase,
  } = useCrmBoardContext();

  const [activeTab, setActiveTab] = useState<TabKey>('orders');

  const { orders, isLoading: ordersLoading } = useRecentOrders(selectedStore?.id ?? null);
  const { issueCases, isLoading: issueCasesLoading } = useRecentIssueCases(selectedStore?.id ?? null);

  const handleStoreSelect = (storeId: string | null, storeName?: string) => {
    if (storeId && storeName) {
      setSelectedStore({ id: storeId, name: storeName });
    } else {
      setSelectedStore(null);
      setLinkedOrder(null);
      setLinkedIssueCase(null);
    }
  };

  // 주문 선택/해제 토글
  const handleOrderSelect = useCallback(
    (order: OrderDoc) => {
      if (linkedOrder?._id === order._id) {
        setLinkedOrder(null);
      } else {
        setLinkedOrder(order);
        setLinkedIssueCase(null); // 주문 선택 시 이슈 연결 해제
      }
    },
    [linkedOrder, setLinkedOrder, setLinkedIssueCase]
  );

  // 이슈 케이스 선택/해제 토글
  const handleIssueCaseSelect = useCallback(
    (issueCase: IssueCaseDoc) => {
      if (linkedIssueCase?._id === issueCase._id) {
        setLinkedIssueCase(null);
      } else {
        setLinkedIssueCase(issueCase);
        setLinkedOrder(null); // 이슈 선택 시 주문 연결 해제
      }
    },
    [linkedIssueCase, setLinkedIssueCase, setLinkedOrder]
  );

  const segmentedOptions = [
    { label: '최근 주문', value: 'orders' },
    { label: '완료된 이슈', value: 'issues' },
  ];

  return (
    <div className={classes.container}>
      <StoreSearch
        selectedStoreId={selectedStore?.id ?? null}
        onStoreSelect={handleStoreSelect}
        placeholder='매장 검색'
      />

      {selectedStore && (
        <>
          <Segmented
            options={segmentedOptions}
            value={activeTab}
            onChange={(value) => setActiveTab(value as TabKey)}
            block
            size='small'
          />
          {activeTab === 'orders' ? (
            <RecentOrderList
              orders={orders}
              isLoading={ordersLoading}
              selectedOrderId={linkedOrder?._id ?? null}
              onOrderSelect={handleOrderSelect}
            />
          ) : (
            <RecentIssueCaseList
              issueCases={issueCases}
              isLoading={issueCasesLoading}
              selectedIssueCaseId={linkedIssueCase?._id ?? null}
              onIssueCaseSelect={handleIssueCaseSelect}
            />
          )}
        </>
      )}
    </div>
  );
};

export default StoreSection;
